import generatePreviousTimingsUpToCurrent from "slate-transcript-editor/util/dpe-to-slate/generate-previous-timings-up-to-current";
import { shortTimecode } from "slate-transcript-editor/util/timecode-converter";

import patienceDiff from "./patience_diff";
import type { SlateParagraph, STTWord } from "./types";

export function validate(word: STTWord): word is Required<STTWord> {
  if (!word) {
    return false;
  }
  if (!word.text) {
    return false;
  }
  // We could have some, or none, but not both
  if (word.start == null || word.end == null) {
    // We have some
    // noinspection RedundantIfStatementJS
    if (word.start != null || word.end != null) {
      // Disallowed, must have all or none
      return false;
      // We have none
    } else {
      // Allowed
      return true;
    }
    // We have both
  } else {
    // End time is before start time, which just doesn't make sense
    // We handle word.end === word.start later on
    if (word.end < word.start) {
      return false;
    }
    // Allowed
    return true;
  }
}

// When we go through fixing a paragraph, we iterate over all the words in
// it and break a sequence of words into chunks. A chunk can be one of two
// types:
//    a) correct timing information - no timing tweak needed
//    b) incorrect timing information - subsequent logic must come in and
//       tweak word start/end times.
//
// Timing may be incorrect due to...
//    i. a word is entered by the user (start/end will be null)
//    ii. a word is 0 duration because Google Translate only has 0.1 sec
//        resolution. This causes some downstream problems (exactly which we
//        do not recall at the time of writing of this comment but we do
//        remember that our goal is to ensure every word has some kind of
//        duration
//
// - boundary == we are processing the first word of a chunk
// - leading_edge == we are entering a region that must be realigned
// - following_edge (i.e boundary && !leading_edge) == we are leaving a
//   region that must be realigned
type BoundaryInfo = {
  boundary: boolean;
  leadingEdge: boolean;
};

// This function looks for boundaries between words that have timing info
// and words that have been manually entered by the user and therefore
// have null start/end times.
function distinctBoundary(wordA: STTWord, wordB: STTWord): BoundaryInfo {
  if (!validate(wordA)) {
    if (validate(wordB) && wordB.start === wordB.end) {
      return {
        boundary: true,
        leadingEdge: true,
      };
    } else {
      return {
        boundary: false,
        leadingEdge: false,
      };
    }
  }
  // Let's assume they're valid -- if start, then end, if no start then no end
  // Basically, does one have time and the other not
  const leadingEdge = wordA.start != null && wordB.start === undefined;
  const followingEdge = wordB.start != null && wordA.start === undefined;

  return {
    boundary: leadingEdge || followingEdge,
    leadingEdge: leadingEdge,
  };
}

// This function looks for boundaries between words whose timing information
// is fine (i.e. doesn't need to be corrected) and segments where words have same
// time/end times.  Due to nature of how we handle transcripts, 0-duration words
// cause grief so we try not to have any
function resolutionErrorBoundary(wordA: STTWord, wordB: STTWord): BoundaryInfo {
  // This is to capture regions that look like
  // start: 1,   end: 1.5
  // start: 1.5, end: 1.5
  // start: 1.5, end: 1.5
  // ...
  // start: 1.5, end: 2

  if (validate(wordA) && validate(wordB)) {
    if (wordA.start === wordA.end) {
      // End of chunk
      const followingEdge = wordB.start === wordA.end && wordB.end !== wordA.end;
      return {
        boundary: followingEdge,
        leadingEdge: false,
      };
    } else if (wordB.start === wordB.end) {
      // Start of chunk
      const leadingEdge = wordA.end === wordB.start;
      return {
        boundary: leadingEdge,
        leadingEdge: leadingEdge,
      };
    }
  }

  return {
    boundary: false,
    leadingEdge: false,
  };
}

function combinedBoundary(prevWord: STTWord, word: STTWord, nextWord: STTWord): BoundaryInfo {
  // We want to start the new chunk from the first unaligned word when on an alignment-missing boundary
  const distinct = distinctBoundary(prevWord, word);

  // But we want to include the edge words from a resolution error boundary
  // so that we can properly redistribute their end/start times to the inner words that are missing resolution
  // (will this just include the leading edge and not the tailing edge?)
  const res = resolutionErrorBoundary(word, nextWord);

  // Lag by 1 word
  const prevRes = resolutionErrorBoundary(prevWord, word);

  return {
    boundary: distinct.boundary || (res.boundary && res.leadingEdge) || (!res.boundary && prevRes.boundary),
    leadingEdge: distinct.leadingEdge || res.leadingEdge,
  };
}

// Sort-of educated guess
// See: https://github.com/pietrop/slate-transcript-editor/blob/9c789d39cbe853c0c58bc3f000c1a68312164956/docs/notes/alternative-alignment-approaches.md#word-time-euristic-based-on-chat-count
// Chris Baume BBC R&D heuristic to estimate duration of a word, based on looking across a number of transcripts.
// from https://github.com/chrisbaume/webaligner/blob/9458df57d854e9df64a54bc23a7f0856de49730f/webaligner.js#L7
// estimates the duration of a word, in seconds
function wordDuration(word: string): number {
  return 0.08475 + 0.05379 * word.length;
}

function updateTokensFromText(text: string, tokens: STTWord[]): STTWord[] {
  const textWords = text.split(" ");
  const tokenWords = tokens.map((t) => t.text);

  const { lines } = patienceDiff(tokenWords, textWords);

  return lines
    .map(({ line: word, aIndex: tokenIndex, bIndex: textIndex }) => {
      if (tokenIndex >= 0 && textIndex >= 0) {
        // Supposedly the same word just shifted, let's use the token so we keep timing info
        return tokens[tokenIndex as number];
      } else if (tokenIndex < 0) {
        // Word was added in text
        return { text: word as string };
      } else if (textIndex < 0) {
        // Word was deleted from text
        return null;
      } else {
        throw new Error("Dragons");
      }
    })
    .filter((t): t is STTWord => !!t);
}

function flagInvalid(word: STTWord): STTWord {
  if (!validate(word)) {
    if (word.text) {
      // console.log(`Encountered invalid word: ${JSON.stringify(word)}. Flagging for realignment`);
      return {
        text: word.text,
      } as STTWord;
    } else {
      // console.log(`Encountered word with no text: ${JSON.stringify(word)}. Stripping it.`);
      // Because literally every edge case happens regularly
      return null;
    }
  } else {
    return { ...word }; // Clone
  }
}

function handleOverlap(word: STTWord, idx: number, arr: STTWord[]): STTWord {
  if (!word) {
    return null;
  }

  if (idx < arr.length - 1 && word.start != null) {
    const nextWord = arr[idx + 1];
    if (!nextWord) {
      // No way to check overlap!
      return word;
    }

    if (word.start === nextWord.start && word.end === nextWord.end) {
      if (word.text === nextWord.text) {
        // Exact overlap, remove one and keep going
        return null;
      } else {
        // Let resolution_error_boundary handle consecutive non-equal overlapping zero-length words (yeesh)
        return word;
      }
      // Handle partial overlap here
    } else if (word.start < nextWord.end && nextWord.start < word.end) {
      const start = word.start;
      const end = nextWord.end;
      const mid = (end - start) / 2 + start;
      word.end = mid;
      nextWord.start = mid;
      return word;
    } else {
      return word;
    }
  } else {
    return word;
  }
}

// As opposed to `handleOverlap` which detects overlapping ranges in adjacent words,
// this function detects out-of-order words anywhere in the word list, and flags them for realignment
function handleWideRangeOutOfOrder(arr: STTWord[]): STTWord[] {
  let startCounter = 0;
  let endCounter = 0;

  return arr.map((w) => {
    if (w.start != null && w.end != null) {
      if (w.start >= startCounter && w.end >= endCounter) {
        startCounter = w.start;
        endCounter = w.end;
        return w;
      } else {
        return { text: w.text };
      }
    } else {
      return w;
    }
  });
}

function createChunks(chunks: STTWord[][], currentWord: STTWord, idx: number, arr: STTWord[]): STTWord[][] {
  const currentChunk = chunks.length > 0 ? chunks[chunks.length - 1] : [];

  const prevWord = arr[idx - 1];
  const nextWord = arr[idx + 1];

  const { boundary, leadingEdge } = combinedBoundary(prevWord, currentWord, nextWord);

  let word: STTWord = currentWord;

  if (leadingEdge || (currentChunk[0] !== undefined && currentChunk[0].start == null && !boundary)) {
    // We're entering or inside an unaligned chunk
    // NOTE: We force start/end to undefined here because of resolution error boundaries
    // which _do_ have start/end times, but we want to forcibly redistribute them
    word = {
      text: currentWord.text,
    };
  }

  if (boundary) {
    chunks.push([word]);
  } else {
    currentChunk.push(word);
    if (chunks.length < 1) {
      chunks.push(currentChunk);
    }
  }

  return chunks;
}

function combineLikeChunks(accum: STTWord[][], currentChunk: STTWord[]): STTWord[][] {
  if (accum.length < 1) {
    accum.push(currentChunk);
  } else {
    const prevChunk = accum[accum.length - 1];
    const prevChunkLastWord = prevChunk[prevChunk.length - 1];
    if (!distinctBoundary(prevChunkLastWord, currentChunk[0]).boundary) {
      // console.debug(
      //   `Combining two adjacent similar chunks: ${prev_chunk.map((c) => c.text).join(" ")} => ${current_chunk
      //     .map((c) => c.text)
      //     .join(" ")}`
      // );
      prevChunk.push(...currentChunk);
    } else {
      accum.push(currentChunk);
    }
  }
  return accum;
}

interface ChunkMetadata {
  chunk: STTWord[];
  doAlign: boolean;
  leftBoundary: number;
  rightBoundary: number;
  availableSpace: number;
  approxDuration: number;
}

const computeChunkMetadata =
  (paragraph: SlateParagraph, nextParagraph?: SlateParagraph) =>
  (chunk: STTWord[], idx: number, arr: STTWord[][]): ChunkMetadata => {
    if (chunk[0].start != null) {
      return {
        chunk,
        doAlign: false,
        leftBoundary: null,
        rightBoundary: null,
        availableSpace: null,
        approxDuration: null,
      };
    }
    const approxDuration = chunk
      // Get all estimated word durations in chunk
      .map((c) => wordDuration(c.text))
      // Add 'em up
      .reduce((acc, curr) => acc + curr, 0);

    // If there are words left of chunk, then left boundary = directly preceeding word's end time, otherwise paragraph start
    let leftBoundary: number;
    if (idx > 0) {
      const preceedingChunk = arr[idx - 1];
      const preceedingWord = preceedingChunk[preceedingChunk.length - 1];
      if (preceedingWord && preceedingWord.end) {
        leftBoundary = preceedingWord.end;
      } else {
        // Because we segment chunks by `distinct_boundary`, which is supposed to identify situations
        // where we transition from aligned to needing alignment, our chunks should alternate aligned and not
        // so if we're in a chunks that needs alignment, and its prior chunk is _missing_ alignment, something's broken
        throw new Error("Unexpected: two chunks in a row needed realignment. Something's up");
      }
    }

    let rightBoundary: number;

    // If there are words to the right of chunk, then right boundary = directly succeeding word's start time
    if (idx < arr.length - 1) {
      // There are chunks after us
      const succeedingChunk = arr[idx + 1];
      const succeedingWord = succeedingChunk[0];
      if (succeedingChunk && succeedingWord.start != null) {
        rightBoundary = succeedingWord.start;
      } else {
        // Same error case as above, but on the other side
        throw new Error("Unexpected: two chunks in a row needed realignment. Something's up");
      }
    } else if (nextParagraph) {
      // if there is a succeeding paragraph, use its start time
      // minus a miniscule amount, so the words don't show up in multiple paragraphs
      rightBoundary = nextParagraph.start;
    } else {
      // otherwise use previously calculated start time + approx. chunk duration
      rightBoundary = leftBoundary ?? paragraph.start + approxDuration;
    }

    if (leftBoundary == null) {
      // we must keep time-forward progression. If paragraph start time is later than right boundary,
      // we know paragraph start time is bad (i.e. some text was copy pasted). Otherwise use the
      // paragraph start time
      leftBoundary = rightBoundary < paragraph.start ? rightBoundary - approxDuration : paragraph.start;
    }

    const availableSpace = rightBoundary - leftBoundary;

    return {
      chunk,
      doAlign: true,
      leftBoundary: leftBoundary,
      rightBoundary: rightBoundary,
      availableSpace: availableSpace,
      approxDuration: approxDuration,
    };
  };

function stealTime(
  { chunk, leftBoundary, rightBoundary, availableSpace, doAlign, approxDuration }: ChunkMetadata,
  idx: number,
  arr: ChunkMetadata[]
): ChunkMetadata {
  // Let's steal words from the left, except if we're in the beginning, where we need to go right

  // Special case: 0th chunk has no available space.
  // So in the 0th chunk, let's extend right_boundary one word to the right,
  // And in 1st chunk we contract right_boundary by the same word length

  if (idx === 0 && doAlign && availableSpace === 0) {
    const nextChunk = arr[idx + 1];

    // We need to check whether we are in fact the _only_ chunk here,
    // otherwise next_word might not exist

    let stolenDuration;

    if (nextChunk) {
      const nextWord = nextChunk.chunk[0];
      // We go with /2 here because we want the word we steal time from to still have some space for itself
      // This is seriously starting to feel like herding cats
      stolenDuration = (nextWord.start != null ? nextWord.end - nextWord.start : wordDuration(nextWord.text)) / 2;
    } else {
      stolenDuration = 0;
    }

    return {
      chunk,
      leftBoundary: leftBoundary,
      rightBoundary: rightBoundary + stolenDuration,
      availableSpace: stolenDuration,
      doAlign: doAlign,
      approxDuration: approxDuration,
    };
  } else if (idx === 1 && arr[0].doAlign && arr[0].availableSpace === 0) {
    const stolenDuration = (chunk[0].start != null ? chunk[0].end - chunk[0].start : wordDuration(chunk[0].text)) / 2;
    // Steal the time if it had time defined (i.e not need realign)
    if (chunk[0].start != null) {
      chunk[0].start += stolenDuration;
    }
    return {
      chunk,
      leftBoundary: leftBoundary + stolenDuration,
      rightBoundary: rightBoundary,
      availableSpace: availableSpace - stolenDuration,
      doAlign: doAlign,
      approxDuration: approxDuration,
    };
  }

  // Ok, now normal operationg procedure

  const { doAlign: nextDoAlign, availableSpace: nextAvailableSpace } = arr[idx + 1] ?? {};
  if (nextDoAlign && nextAvailableSpace === 0) {
    // The next one needs to steal, so we preemptively give up some space
    const lastWord = chunk[chunk.length - 1];
    const stolenDuration = (lastWord.start != null ? lastWord.end - lastWord.start : wordDuration(lastWord.text)) / 2;

    if (lastWord.start != null) {
      lastWord.end -= stolenDuration;
    }
    return {
      chunk,
      leftBoundary: leftBoundary,
      rightBoundary: rightBoundary - stolenDuration,
      availableSpace: availableSpace - stolenDuration,
      doAlign: doAlign,
      approxDuration: approxDuration,
    };
  } else if (doAlign && availableSpace === 0) {
    // Ok finally we do the stealing from the previous chunk
    const prevChunk = arr[idx - 1].chunk;
    const prevWord = prevChunk[prevChunk.length - 1];

    const stolenDuration = (prevWord.start != null ? prevWord.end - prevWord.start : wordDuration(prevWord.text)) / 2;

    return {
      chunk,
      leftBoundary: leftBoundary - stolenDuration,
      rightBoundary,
      availableSpace: stolenDuration,
      doAlign,
      approxDuration,
    };
  }

  return {
    chunk,
    leftBoundary,
    rightBoundary,
    availableSpace,
    doAlign,
    approxDuration,
  };
}

function guessTimes({ chunk, leftBoundary, availableSpace, doAlign, approxDuration }: ChunkMetadata): STTWord[] {
  // This is a chunk needing realignment
  if (doAlign) {
    // Figure out how to assign timecodes to new words
    // NOTE: I'm going to try and be fancy by always computing the weighted timecodes,
    // and then scaling by the appropriate factor if space is constrained.
    // ...Wish me luck

    // We don't want to scale up here, we only want to constrict if needed
    const scalingFactor = Math.min(1, availableSpace / approxDuration);

    let wordStart = leftBoundary;
    return chunk.map(
      // eslint-disable-next-line no-return-assign
      (word) =>
        ({
          ...word,
          start: wordStart,
          end: (wordStart += wordDuration(word.text) * scalingFactor),
        } as Required<STTWord>)
    );
  } else {
    // No alignment needed!
    return chunk;
  }
}

/*
 * Fixes timing information of the word array within a paragraph
 *
 * Execution steps:
 *   1) Look for overlapping words. In order to keep transcript editor happy, we cannot have words
 *      that have 0-duration or which are overlapping (partially or fully) with other words. If we
 *      find such words...
 *     IF full_overlap(w1,w2) => nuke timing info
 *     IF partial_overlap(w1,w2) => evenly split, so that...
 *         w1={
 *               start:min(all),
 *               end:(max(all)-min(all))/2 + min(all)
 *         },
 *         w2={
 *               start:(max(all)-min(all))/2 + min(all),
 *               end: max(all)
 *         }
 *
 *   2) Fix up any words that are missing timing info (as a result of previous steps or as a result
 *      of user edits where paragraph "text" no longer matches paragraph's word list)
 *     - Group into chunks, where each chunk contains a continuous group where ALL words have good
 *       timing info (i.e. no change needed) or ALL words are missing timing info (i.e. word timings
 *       need be extrapolated based on chunk boundaries)
 *     - Calculate rough approx. duration of chunk
 *     - Figure out how to fit chunk into paragraph, without shifting existing paragraph or word
 *       timings -- we're assuming that the existing word timings are reliable, as they usually are.
 *       - If there are words left of chunk, then left boundary = directly preceeding word's end
 *         time, otherwise paragraph start
 *       - If there are words to the right of chunk, then right boundary = directly succeeding word's
 *         start time, otherwise...
 *             - if there is a succeeding paragraph, use its start time
 *             - otherwise use previously calculated start time + approx. chunk duration
 *     - Figure out how to assign timecodes to new words
 *         - IF Space available < estimated chunk duration, then evenly (later weight this by
 *           heuristic for word len in seconds) distribute time to each word in chunk
 *         - Otherwise, distribute (start, start+estimated duration) to each word, leaving remaining
 *           free space for later
 *     - Place chunk into word array at right place
 */
export function fixParagraph({
  paragraph,
  nextParagraph,
  textOverride,
}: {
  paragraph: SlateParagraph;
  nextParagraph?: SlateParagraph;
  textOverride?: string;
}): SlateParagraph {
  let words: STTWord[];

  if (textOverride) {
    words = updateTokensFromText(
      textOverride,
      paragraph.children.flatMap((c) => c.words)
    );
  } else {
    // Flatten all children into a list of words
    // Let's _assume_ that words are ordered, and children are ordered such that child 2's word array starts where child 1's ends
    words = paragraph.children.flatMap((c) => updateTokensFromText(c.text, c.words));
  }

  // for some reason, we're getting non-breaking spaces (U+00A0) in the text. We need to remove them to avoid issues saving
  words = words.map((word) => ({
    ...word,
    text: word.text.replace(/\u00A0/g, "").trim(),
  }));

  let alignedWords = words
    // If our word is inconsistent or invalid (missing a start/end but not both, end < start), flag it for realignment
    .map(flagInvalid)
    // Identify words that are overlapping other words, and flag them for realignment
    .map(handleOverlap)
    // Let's get rid of those pesky nulls left over from the mapping above
    .filter((t): t is Required<STTWord> => !!t && t.text.length > 0);

  // Flag words that are out of order beyond their direct neighbors for realignment
  // We have to break the chain here because for efficiency reasons
  // `handleWideRangeOutOfOrder` can't operate on a word at a time
  // (because otherwise it would be duplicating tons of work)
  alignedWords = handleWideRangeOutOfOrder(alignedWords)
    // Create alternating chunks of [aligned, unaligned, aligned, unaligned]
    // This way we can figure out the start/end time of the chunks in order to properly interpolate
    // words that are missing timing info (i.e flagged)
    .reduce(createChunks, [] as STTWord[][])
    // Sometimes `createChunks` creates multiple adjacent chunks that need, or don't need alignment
    // This happens because there are two different conditions that can cause a word to be in/out of a chunk
    // and all of the various different input conditions here sometimes cause it to make chunks that need to be combined
    // note: testing `createChunks` fully should probably eliminate the nee for this
    .reduce(combineLikeChunks, [] as STTWord[][])
    // Calculate left/right boundaries + duration
    .map(computeChunkMetadata(paragraph, nextParagraph))
    // If available_space == 0, we need to decide where to steal time from.
    // NOTE: Let's just steal one word's worth of time
    // It'll result in a pretty high scaling factor (i.e we're fitting more words into the same time)
    // But that's okay because technically these words don't fit anywhere in time
    // as in, you added words without also deleting words
    .map(stealTime)
    // Now let's try to fill in timecodes for missing chunks
    // NOTE: We aren't just flatMapping here because the time-stealing feature
    // might mutate the array
    .map(guessTimes)
    .flat() as Required<STTWord>[];

  let { previousTimings, start, startTimecode } = paragraph;
  if (alignedWords.length > 0 && alignedWords[0].start !== start) {
    start = alignedWords[0].start;
    startTimecode = shortTimecode(start) as string;
    previousTimings = generatePreviousTimingsUpToCurrent(start) as string;
  }

  return {
    speaker: paragraph.speaker,
    start,
    previousTimings,
    startTimecode,
    type: paragraph.type,
    children: [
      {
        text: alignedWords.map((w) => w?.text).join(" "),
        words: alignedWords,
      },
    ],
  };
}
